import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { TrendingUp as TrendingUpIcon, Warning as WarningIcon, Speed as SpeedIcon } from '@mui/icons-material';

const MacroSummaryChart = ({ macroScore }) => {
  const { t } = useTranslation();
  if (!macroScore) return null;

  // Prepare pillar data for bar chart
  const pillarData = macroScore.pillar_scores?.map((pillar) => ({
    code: pillar.pillar_code,
    name: pillar.pillar_name?.split('/')[0]?.trim() || pillar.pillar_code,
    score: parseFloat(pillar.pillar_score || pillar.raw_score || 0),
    weight: parseFloat(pillar.pillar_weight || 0) * 100,
    contribution: parseFloat(pillar.final_contribution || 0),
    isRisk: pillar.pillar_code === 'H',
  })) || [];

  // Sort by contribution (descending)
  const sortedPillarData = [...pillarData].sort((a, b) => b.contribution - a.contribution);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 2,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            {data.name} ({data.code})
          </Typography>
          <Typography variant="body2" sx={{ color: data.isRisk ? '#ff6b6b' : '#667eea', fontWeight: 700 }}>
            {t('dashboard.macroSummary.score')}: {data.score.toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('dashboard.macroSummary.weight')}: {data.weight.toFixed(1)}%
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('dashboard.macroSummary.contribution')}: {data.contribution.toFixed(2)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const mos = parseFloat(macroScore.mos || macroScore.macro_score || 0);
  const mrs = parseFloat(macroScore.mrs || 0);
  const netScore = parseFloat(macroScore.net_score || macroScore.macro_score || 0);

  return (
    <Card 
      elevation={0}
      sx={{ 
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'background.paper',
          boxShadow: 'none',
        },
        transition: 'none',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Header with Score Badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                mr: 2,
              }}
            >
              <SpeedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('dashboard.macroSummary.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.macroSummary.subtitle', { k: macroScore.k_value })}
              </Typography>
            </Box>
          </Box>
          
          {/* Score Badges */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {/* MOS Badge */}
            <Box
              sx={{
                minWidth: 80,
                p: 1.25,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.25 }}>
                <TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  MOS
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {mos.toFixed(1)}
              </Typography>
            </Box>
            
            {/* MRS Badge */}
            <Box
              sx={{
                minWidth: 80,
                p: 1.25,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: 'white',
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.25 }}>
                <WarningIcon sx={{ fontSize: 14, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  MRS
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {mrs.toFixed(1)}
              </Typography>
            </Box>
            
            {/* Net Score Badge */}
            <Box
              sx={{
                minWidth: 80,
                p: 1.25,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.25 }}>
                <SpeedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  NET
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {netScore.toFixed(1)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Pillar Contribution Bar Chart */}
        <Box sx={{ width: '100%', height: 280 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={400} minHeight={200}>
            <BarChart data={sortedPillarData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="code" 
                tick={{ fill: '#666', fontSize: 12 }}
              />
              <YAxis 
                tick={{ fill: '#999', fontSize: 11 }}
                label={{ value: t('dashboard.macroSummary.pillarScore'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#999' } }}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} wrapperStyle={{ zIndex: 1000 }} animationDuration={50} animationEasing="ease-out" />
              <ReferenceLine y={50} stroke="#999" strokeDasharray="3 3" label={{ value: '50', position: 'right', fill: '#999', fontSize: 10 }} />
              <Bar dataKey="score" radius={[8, 8, 0, 0]} activeBar={false}>
                {sortedPillarData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.isRisk ? '#ff6b6b' : '#667eea'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend and Formulas */}
        <Box sx={{ 
          mt: 2, 
          pt: 1.5, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {t('dashboard.macroSummary.basedOn', { count: macroScore.total_indicators })} • {t('dashboard.macroSummary.updated')}: {new Date(macroScore.calculation_date).toLocaleDateString()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="caption" sx={{ color: '#11998e', fontWeight: 600 }}>
                • {t('dashboard.macroSummary.mosFormula')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#ff6b6b', fontWeight: 600 }}>
                • {t('dashboard.macroSummary.mrsFormula')}
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary', fontWeight: 600 }}>
            {t('dashboard.macroSummary.netScoreFormula')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MacroSummaryChart;

